/*
 * Top progress bar.
 *
 * This is a multi-page store — every navigation is a real page load — so the bar cannot
 * track actual progress. It starts when a navigation is committed to and disappears when
 * the new document replaces this one. It eases toward 90% and HOLDS: "complete" is the
 * next page arriving, not an animation finishing. Its whole job is turning the dead
 * 300–800ms after a tap into visible feedback.
 *
 * ⚠️ The element is created ONCE, at load, and only a class is toggled per navigation.
 * The obvious approach — build the div on click — does not reliably paint: inserting a
 * node and navigating in the same task can finish teardown before the browser ever
 * renders a frame, so on a fast connection the bar appeared to do nothing at all.
 * Pre-existing element + a CSS keyframe animation means starting costs one class change,
 * the layout is already computed, and the animation runs on the compositor.
 *
 * ⚠️ The other hard part is NOT showing it for clicks that never navigate, and this theme
 * is full of those — search, cart, quick-add, wishlist, mega menu, every bottom-nav tab
 * are links and buttons whose default is cancelled by JS. A bar that appears and then
 * hangs forever is worse than no bar. The fix is to listen on `document` in the BUBBLE
 * phase and bail when `event.defaultPrevented` is set: by the time a click reaches
 * document, every one of those interceptors has already run and cancelled it. No
 * allow-list to maintain, and anything added later is handled for free.
 */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const bar = document.createElement('div');
  bar.className = 'sd-progress';
  bar.setAttribute('role', 'progressbar');
  bar.setAttribute('aria-label', 'Loading');
  bar.setAttribute('aria-hidden', 'true');

  function mount() {
    if (!bar.isConnected) document.body.appendChild(bar);
  }
  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount);

  function start() {
    mount();
    // Restart the animation even if the class is already there (second click, or a
    // navigation right after a cancelled one): removing the class, forcing a reflow and
    // re-adding it is the standard way to replay a CSS animation.
    bar.classList.remove('is-loading');
    void bar.offsetWidth;
    bar.classList.add('is-loading');
  }

  function stop() {
    bar.classList.remove('is-loading', 'is-done');
  }

  /* Run to 100% and fade out. */
  function finish() {
    if (!bar.classList.contains('is-loading')) return;
    bar.classList.remove('is-loading');
    void bar.offsetWidth;
    bar.classList.add('is-done');
  }

  // Exposed so the bar can be checked without waiting on a real navigation:
  //   SdProgress.start()  in the console.
  window.SdProgress = { start, stop, finish };

  /* ---- The INCOMING half of a navigation ---------------------------------------
     A bar started on the previous page dies with that document, and it only ever
     covered click → new-HTML-arrives (~50ms locally). Everything a shopper actually
     watches loading — images, fonts, scripts — happens AFTER that, in this document.
     So pick the bar back up here and carry it until `load`, which is the point the
     browser's own tab spinner stops too.
     `readyState` guard: if the script somehow runs after load there is nothing left
     to wait for, and a bfcache restore is already 'complete'. */
  if (document.readyState !== 'complete') {
    start();
    window.addEventListener('load', finish, { once: true });
  }

  /* Would this click actually navigate this tab? */
  function navigates(event, link) {
    // Cancelled by one of the theme's own handlers (search panel, cart drawer,
    // quick-add, wishlist, mega menu, bottom-nav proxies).
    if (event.defaultPrevented) return false;
    // Middle-click and modifier-click open a new tab; this one stays put.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#')) return false;
    // mailto:, tel:, javascript: — hand off to the OS or do nothing.
    if (/^[a-z][a-z0-9+.-]*:/i.test(href) && !/^https?:/i.test(href)) return false;

    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin) return false;
    // Same page plus a different hash is an in-page jump, not a navigation.
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return false;

    return true;
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (link && navigates(event, link)) start();
  });

  // Filter, sort and search forms navigate too.
  document.addEventListener('submit', (event) => {
    if (!event.defaultPrevented) start();
  });

  // Restored from the back/forward cache. Without this you return from a product page to
  // a bar frozen mid-animation, because the DOM comes back exactly as it left.
  window.addEventListener('pageshow', stop);
})();
